import { type DriveKind, type DriveLinkItem, type FileItem, MAX_UPLOAD_BYTES, type MarkupShape, type UploadSlots } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useRef, useState } from "react";
import { Link } from "react-router";
import { del, errorMessage, get, patch, post } from "../api";
import { formatBytes, isImage, photoInfo } from "../lib/images";
import { putBlob } from "../lib/upload";
import { useSession } from "../session";
import { MarkedPhoto, MarkupEditor } from "./markup";
import { Alert, Button, Checkbox, Dialog, Empty, Field, Loading, Panel, PanelHeader, SelectField, cx, formatDateTime } from "./ui";

type Owner = { kind: "job" | "contact" | "company"; id: string };
type Upload = { name: string; state: "working" | "done" | "failed"; error?: string };
type View = "all" | "photos" | "documents";

const base = (o: Owner) => (o.kind === "job" ? `/api/jobs/${o.id}/files` : o.kind === "contact" ? `/api/contacts/${o.id}/files` : "/api/company/files");


/** Files and photos on a job or a contact: upload (button or drag and drop), photo grid, documents list. */
export function FilesPanel({ owner, readOnly, badges, docAction }: { owner: Owner; readOnly?: boolean; /** Labels shown on a document (a sub's "W-9", "Workers' comp"). */ badges?: Record<string, string[]>; /** An extra button on each document. */ docAction?: (f: FileItem) => ReactNode }) {
  const { can, me } = useSession();
  const qc = useQueryClient();
  const queryKey = ["files", owner.kind, owner.id];
  const files = useQuery({ queryKey, queryFn: () => get<FileItem[]>(base(owner)) });
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [view, setView] = useState<View>("all");
  const [category, setCategory] = useState("");
  const [open, setOpen] = useState<FileItem | null>(null);
  const [dragging, setDragging] = useState(false);
  const [reporting, setReporting] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const canUpload = !readOnly && can("files.upload");
  const categories = me?.lists.fileCategories ?? [];

  async function uploadAll(list: File[]) {
    const tooBig = list.filter((f) => f.size > MAX_UPLOAD_BYTES || f.size === 0);
    const ok = list.filter((f) => !tooBig.includes(f));
    setUploads((u) => [
      ...u.filter((x) => x.state === "working"),
      ...tooBig.map((f) => ({ name: f.name, state: "failed" as const, error: f.size ? "Too large (200 MB limit)" : "Empty file" })),
      ...ok.map((f) => ({ name: f.name, state: "working" as const })),
    ]);
    if (!ok.length) return;
    const mark = (name: string, state: Upload["state"], error?: string) => setUploads((u) => u.map((x) => (x.name === name && x.state === "working" ? { ...x, state, error } : x)));
    let slots: UploadSlots[];
    try {
      slots = await post<UploadSlots[]>(`${base(owner)}/upload-urls`, {
        files: ok.map((f) => ({ name: f.name, contentType: f.type || "application/octet-stream", size: f.size, variants: isImage(f.type, f.name) })),
      });
    } catch (e) {
      for (const f of ok) mark(f.name, "failed", errorMessage(e));
      return;
    }
    // A few at a time: phones upload slowly and photos come in dozens.
    const queue = ok.map((f, i) => ({ f, slot: slots[i]! }));
    const worker = async () => {
      for (let next = queue.shift(); next; next = queue.shift()) {
        const { f, slot } = next;
        try {
          const photo = isImage(f.type, f.name);
          const info = photo ? await photoInfo(f) : null;
          await putBlob(slot.original, f);
          // Copies are best effort: without them the original shows.
          if (info?.thumb && slot.thumb) await putBlob(slot.thumb, info.thumb).catch(() => undefined);
          if (info?.medium && slot.medium) await putBlob(slot.medium, info.medium).catch(() => undefined);
          await post(base(owner), {
            key: slot.original.key,
            thumbKey: slot.thumb?.key ?? null,
            mediumKey: slot.medium?.key ?? null,
            name: f.name,
            contentType: f.type || "application/octet-stream",
            size: f.size,
            isPhoto: photo,
            category: category || (photo ? "Photos" : ""),
            width: info?.width ?? null,
            height: info?.height ?? null,
            takenAt: info?.takenAt ?? null,
            latitude: info?.latitude ?? null,
            longitude: info?.longitude ?? null,
          });
          mark(f.name, "done");
        } catch (e) {
          mark(f.name, "failed", errorMessage(e));
        }
      }
    };
    await Promise.all([worker(), worker(), worker()]);
    await qc.invalidateQueries({ queryKey });
  }

  const all = files.data ?? [];
  const shown = all.filter((f) => (view === "photos" ? f.isPhoto : view === "documents" ? !f.isPhoto : true) && (!category || f.category === category));
  const photos = shown.filter((f) => f.isPhoto);
  // Photos without small copies (copied from JobNimbus): the browser can make them.
  const bare = canUpload ? all.filter((f) => f.isPhoto && !f.urls.thumb && /^image\/(jpeg|png|webp|gif)$/.test(f.contentType)) : [];
  const [making, setMaking] = useState<{ done: number; failed: number } | null>(null);
  async function makeCopies() {
    let done = 0;
    let failed = 0;
    setMaking({ done, failed });
    for (const f of bare) {
      try {
        const blob = await (await fetch(f.urls.original)).blob();
        const info = await photoInfo(new File([blob], f.name, { type: f.contentType }));
        if (!info.thumb || !info.medium) throw new Error("unreadable");
        const slots = await post<{ thumb: { key: string; url: string; headers: Record<string, string> }; medium: { key: string; url: string; headers: Record<string, string> } }>(`/api/files/${f.id}/variant-urls`);
        await putBlob(slots.thumb, info.thumb);
        await putBlob(slots.medium, info.medium);
        await post(`/api/files/${f.id}/variants`, { thumbKey: slots.thumb.key, mediumKey: slots.medium.key, width: info.width, height: info.height });
        done++;
      } catch {
        failed++;
      }
      setMaking({ done, failed });
    }
    await qc.invalidateQueries({ queryKey });
  }
  const docs = shown.filter((f) => !f.isPhoto);
  const working = uploads.filter((u) => u.state === "working").length;

  return (
    <Panel>
      <div
        onDragOver={(e) => {
          if (!canUpload) return;
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          if (!canUpload) return;
          e.preventDefault();
          setDragging(false);
          void uploadAll([...e.dataTransfer.files]);
        }}
        className={cx("rounded-[var(--radius-panel)]", dragging && "ring-2 ring-brand ring-offset-2")}
      >
        <PanelHeader
          title="Files and photos"
          description={canUpload ? "Drop files here, or choose them. Photos keep their original; a smaller copy is made for viewing." : undefined}
          action={
            canUpload && (
              <>
                <input
                  ref={input}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    void uploadAll([...(e.target.files ?? [])]);
                    e.target.value = "";
                  }}
                />
                <div className="flex flex-wrap gap-2">
                  {owner.kind === "job" && all.length > 0 && (
                    <a
                      href={`/api/jobs/${owner.id}/files.zip${shown.length < all.length && shown.length <= 200 ? `?ids=${shown.map((f) => f.id).join(",")}` : ""}`}
                      className="inline-flex items-center rounded-[var(--radius-control)] border border-line bg-white px-3 py-1.5 text-sm font-semibold hover:border-ink-soft"
                    >
                      Download {shown.length < all.length ? `${shown.length} shown` : "all"}
                    </a>
                  )}
                  {owner.kind === "job" && all.some((f) => f.isPhoto) && (
                    <Button variant="secondary" className="px-3 py-1.5" onClick={() => setReporting(true)}>
                      Photo report
                    </Button>
                  )}
                  {owner.kind === "job" && (
                    <Link to={`/camera?job=${owner.id}`} className="inline-flex items-center rounded-[var(--radius-control)] border border-line bg-white px-3 py-1.5 text-sm font-semibold hover:border-ink-soft">
                      Take photos
                    </Link>
                  )}
                  <Button variant="primary" className="px-3 py-1.5" onClick={() => input.current?.click()} busy={working > 0}>
                    Upload
                  </Button>
                </div>
              </>
            )
          }
        />
        <div className="flex flex-wrap items-end gap-3 border-b border-mist px-5 py-3">
          <div role="radiogroup" aria-label="Show" className="inline-flex rounded-[var(--radius-control)] bg-mist p-0.5">
            {(["all", "photos", "documents"] as const).map((v) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={view === v}
                onClick={() => setView(v)}
                className={cx("rounded-[6px] px-3 py-1 text-sm font-semibold capitalize", view === v ? "bg-white shadow-sm" : "text-muted hover:text-ink")}
              >
                {v}
              </button>
            ))}
          </div>
          <SelectField label="Category" value={category} onChange={(e) => setCategory(e.target.value)} className="min-w-48" hint={canUpload ? "New uploads go here" : undefined}>
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </SelectField>
        </div>
        {bare.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 border-b border-mist bg-mist/50 px-5 py-2 text-sm">
            <span className="text-ink-soft">
              {making ? `Making small copies: ${making.done} of ${bare.length}${making.failed ? `, ${making.failed} couldn't be read` : ""}…` : `${bare.length} photo${bare.length === 1 ? " has" : "s have"} no small copy yet (copied from JobNimbus), so ${bare.length === 1 ? "it loads" : "they load"} slowly.`}
            </span>
            {!making && (
              <button className="font-semibold text-brand hover:underline" onClick={() => void makeCopies().finally(() => setMaking(null))}>
                Make them now
              </button>
            )}
          </div>
        )}
        {!!uploads.length && (
          <ul className="flex flex-col gap-1 border-b border-mist px-5 py-3 text-sm">
            {uploads.map((u, i) => (
              <li key={`${u.name}-${i}`} className="flex justify-between gap-3">
                <span className="truncate">{u.name}</span>
                <span className={cx("shrink-0", u.state === "failed" ? "text-danger" : u.state === "done" ? "text-ok" : "text-muted")}>
                  {u.state === "working" ? "Uploading…" : u.state === "done" ? "Done" : (u.error ?? "Failed")}
                </span>
              </li>
            ))}
            {!working && (
              <li>
                <button className="text-muted hover:text-ink" onClick={() => setUploads([])}>
                  Clear
                </button>
              </li>
            )}
          </ul>
        )}
        {files.isLoading ? (
          <div className="px-5">
            <Loading />
          </div>
        ) : files.error ? (
          <div className="p-5">
            <Alert>{errorMessage(files.error)}</Alert>
          </div>
        ) : !shown.length ? (
          <Empty>{all.length ? "Nothing matches." : "No files yet."}</Empty>
        ) : (
          <div className="flex flex-col gap-4 px-5 py-4">
            {!!photos.length && (
              <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                {photos.map((f) => (
                  <li key={f.id}>
                    <button className="group relative block aspect-square w-full overflow-hidden rounded-[var(--radius-control)] bg-mist" onClick={() => setOpen(f)}>
                      <img src={f.urls.thumb ?? f.urls.original} alt={f.caption || f.name} loading="lazy" className="size-full object-cover transition-transform group-hover:scale-105" />
                      {!!f.markup.length && <span className="absolute top-1 right-1 rounded bg-brand px-1.5 py-0.5 text-xs font-semibold text-white">Marked</span>}
                      {!!f.tags.length && (
                        <span className="absolute bottom-1 left-1 rounded bg-ink/70 px-1.5 py-0.5 text-xs font-medium text-white">{f.tags.join(", ")}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!!docs.length && (
              <ul className="divide-y divide-mist">
                {docs.map((f) => (
                  <li key={f.id} className="flex items-center gap-3 py-2">
                    <span className="grid size-9 shrink-0 place-items-center rounded bg-mist text-xs font-bold text-ink-soft uppercase">{f.name.split(".").pop()?.slice(0, 4)}</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <a href={f.urls.original} target="_blank" rel="noreferrer" className="truncate font-semibold hover:text-brand">
                          {f.name}
                        </a>
                        {badges?.[f.id]?.map((b) => (
                          <span key={b} className="shrink-0 rounded bg-ok-wash px-1.5 py-0.5 text-xs font-semibold text-ok">
                            {b}
                          </span>
                        ))}
                      </div>
                      <div className="truncate text-xs text-muted">
                        {[f.category, formatBytes(f.sizeBytes), formatDateTime(f.createdAt, me?.company.timezone), f.uploadedBy?.name].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    {!readOnly && docAction?.(f)}
                    <button className="shrink-0 rounded px-2 py-1 text-sm text-muted hover:bg-mist hover:text-ink" onClick={() => setOpen(f)}>
                      Details
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      {view !== "photos" && <DriveLinks owner={owner} canEdit={canUpload} category={category} />}
      {open && <FileDialog file={open} readOnly={!canUpload} onClose={() => setOpen(null)} onChanged={() => qc.invalidateQueries({ queryKey })} />}
      {reporting && <PhotoReportDialog jobId={owner.id} photos={all.filter((f) => f.isPhoto)} onClose={() => setReporting(false)} onMade={() => qc.invalidateQueries({ queryKey })} />}
    </Panel>
  );
}

/** One file: preview, details, and its category, tags, and caption. */
function FileDialog({ file, readOnly, onClose, onChanged }: { file: FileItem; readOnly: boolean; onClose: () => void; onChanged: () => void }) {
  const { me, can } = useSession();
  const [category, setCategory] = useState(file.category);
  const [tags, setTags] = useState(file.tags);
  const [caption, setCaption] = useState(file.caption);
  const [markup, setMarkup] = useState<MarkupShape[]>(file.markup);
  const [marking, setMarking] = useState(false);
  const changed = category !== file.category || caption !== file.caption || tags.join() !== file.tags.join() || JSON.stringify(markup) !== JSON.stringify(file.markup);
  const save = useMutation({
    mutationFn: () => patch(`/api/files/${file.id}`, { category, tags, caption, markup }),
    onSuccess: () => {
      onChanged();
      onClose();
    },
  });
  const suggest = useMutation({
    mutationFn: () => post<{ tags: string[]; caption: string }>(`/api/files/${file.id}/suggest-tags`),
    onSuccess: (r) => {
      setTags((t) => [...new Set([...t, ...r.tags])]);
      setCaption((c) => c || r.caption);
    },
  });
  const remove = useMutation({
    mutationFn: () => del(`/api/files/${file.id}`),
    onSuccess: () => {
      onChanged();
      onClose();
    },
  });
  const [shareLink, setShareLink] = useState<{ url: string; expiresAt: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const share = useMutation({ mutationFn: () => post<{ url: string; expiresAt: string }>(`/api/files/${file.id}/share`, { days: 30 }), onSuccess: (r) => (setShareLink(r), setCopied(false)) });
  const unshare = useMutation({ mutationFn: () => del(`/api/files/${file.id}/share`), onSuccess: () => setShareLink(null) });
  const tz = me?.company.timezone;
  const tagChoices = [...new Set([...(me?.lists.photoTags ?? []), ...file.tags])];
  const mapLink = file.latitude !== null && file.longitude !== null ? `https://www.google.com/maps?q=${file.latitude},${file.longitude}` : null;

  return (
    <Dialog
      wide
      title={file.name}
      onClose={onClose}
      footer={
        <>
          {(file.mine || can("records.delete")) && !readOnly && (
            <Button
              variant="danger"
              className="mr-auto"
              busy={remove.isPending}
              onClick={() => {
                if (window.confirm(`Delete ${file.name}?`)) remove.mutate();
              }}
            >
              Delete
            </Button>
          )}
          {!readOnly && (
            <Button variant="quiet" busy={share.isPending} onClick={() => share.mutate()}>
              Share link
            </Button>
          )}
          <a href={file.urls.download} className="inline-flex items-center rounded-[var(--radius-control)] px-4 py-2 text-sm font-semibold text-ink hover:bg-mist">
            Download original
          </a>
          {!readOnly && (
            <Button variant="primary" busy={save.isPending} disabled={!changed} onClick={() => save.mutate()}>
              Save
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {(save.error || remove.error || share.error || unshare.error) && <Alert>{errorMessage(save.error ?? remove.error ?? share.error ?? unshare.error)}</Alert>}
        {shareLink && (
          <div className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-line p-3 text-sm">
            <div>
              Anyone with this link can see the original, full size, until {formatDateTime(shareLink.expiresAt, tz)}. Paste it into an email or text.
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input readOnly value={shareLink.url} className="min-w-0 flex-1 rounded border border-line px-2 py-1 text-xs" onFocus={(e) => e.target.select()} />
              <Button
                variant="secondary"
                className="px-3 py-1"
                onClick={() => {
                  void navigator.clipboard?.writeText(shareLink.url).then(() => setCopied(true));
                }}
              >
                {copied ? "Copied" : "Copy"}
              </Button>
              <Button variant="quiet" className="px-3 py-1" busy={unshare.isPending} onClick={() => unshare.mutate()}>
                Turn off this file's links
              </Button>
            </div>
          </div>
        )}
        {file.isPhoto ? (
          marking ? (
            <MarkupEditor src={file.urls.medium ?? file.urls.original} alt={file.caption || file.name} shapes={markup} onChange={setMarkup} className="max-h-[55dvh] max-w-full" />
          ) : (
            <div className="flex flex-col items-center gap-2 rounded-[var(--radius-control)] bg-mist">
              <a href={file.urls.original} target="_blank" rel="noreferrer">
                <MarkedPhoto src={file.urls.medium ?? file.urls.original} alt={file.caption || file.name} shapes={markup} className="max-h-[55dvh] max-w-full" />
              </a>
              {!readOnly && (
                <button type="button" className="mb-2 text-sm font-semibold text-brand hover:underline" onClick={() => setMarking(true)}>
                  {markup.length ? "Edit markup" : "Mark up"}
                </button>
              )}
            </div>
          )
        ) : file.contentType === "application/pdf" ? (
          <iframe src={file.urls.original} title={file.name} className="h-[55dvh] w-full rounded-[var(--radius-control)] border border-line" />
        ) : null}
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          {file.takenAt && (
            <>
              <dt className="text-muted">Taken</dt>
              <dd>{formatDateTime(file.takenAt, tz)}</dd>
            </>
          )}
          {mapLink && (
            <>
              <dt className="text-muted">Where</dt>
              <dd>
                <a href={mapLink} target="_blank" rel="noreferrer" className="text-brand hover:underline">
                  {file.latitude!.toFixed(5)}, {file.longitude!.toFixed(5)}
                </a>
              </dd>
            </>
          )}
          <dt className="text-muted">Added</dt>
          <dd>
            {formatDateTime(file.createdAt, tz)}
            {file.uploadedBy && ` by ${file.uploadedBy.name}`}
          </dd>
          <dt className="text-muted">Size</dt>
          <dd>
            {formatBytes(file.sizeBytes)}
            {file.width && file.height && ` · ${file.width} × ${file.height}`}
          </dd>
        </dl>
        {readOnly ? (
          <div className="text-sm">
            {[file.category, file.tags.join(", "), file.caption].filter(Boolean).join(" · ")}
          </div>
        ) : (
          <>
            <SelectField label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">None</option>
              {[...new Set([...(me?.lists.fileCategories ?? []), file.category].filter(Boolean))].map((c) => (
                <option key={c}>{c}</option>
              ))}
            </SelectField>
            {file.isPhoto && (
              <fieldset>
                <legend className="mb-1 flex w-full items-center justify-between text-sm font-semibold text-ink-soft">
                  Tags
                  <button type="button" className="text-xs font-semibold text-brand hover:underline disabled:opacity-50" disabled={suggest.isPending} onClick={() => suggest.mutate()}>
                    {suggest.isPending ? "Looking…" : "Suggest tags and caption"}
                  </button>
                </legend>
                {suggest.error && <p className="mb-1 text-sm text-danger">{errorMessage(suggest.error)}</p>}
                <div className="flex flex-wrap gap-1.5">
                  {tagChoices.map((t) => {
                    const on = tags.includes(t);
                    return (
                      <button
                        key={t}
                        type="button"
                        aria-pressed={on}
                        onClick={() => setTags(on ? tags.filter((x) => x !== t) : [...tags, t])}
                        className={cx("rounded-full px-3 py-1 text-sm font-medium", on ? "bg-brand text-white" : "bg-mist text-ink-soft hover:text-ink")}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              </fieldset>
            )}
            <Field label="Caption" value={caption} onChange={(e) => setCaption(e.target.value)} />
          </>
        )}
      </div>
    </Dialog>
  );
}

/** Picks which photos go in a photo report (by tag), then makes the PDF and saves it to the job's files. */
function PhotoReportDialog({ jobId, photos, onClose, onMade }: { jobId: string; photos: FileItem[]; onClose: () => void; onMade: () => void }) {
  const { me } = useSession();
  const [title, setTitle] = useState("Photo report");
  const [tags, setTags] = useState<string[]>([]);
  const [captions, setCaptions] = useState(true);
  const tagChoices = [...new Set([...(me?.lists.photoTags ?? []).filter((t) => photos.some((p) => p.tags.includes(t))), ...photos.flatMap((p) => p.tags)])];
  const count = tags.length ? photos.filter((p) => p.tags.some((t) => tags.includes(t))).length : photos.length;
  const make = useMutation({
    mutationFn: () => post<FileItem>(`/api/jobs/${jobId}/photo-report`, { title, tags, captions }),
    onSuccess: (f) => {
      onMade();
      window.open(f.urls.original, "_blank", "noopener");
      onClose();
    },
  });
  return (
    <Dialog
      title="Photo report"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={make.isPending} disabled={!count} onClick={() => make.mutate()}>
            Make the PDF ({count} photo{count === 1 ? "" : "s"})
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {make.error && <Alert>{errorMessage(make.error)}</Alert>}
        <Field label="Title" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />
        {!!tagChoices.length && (
          <fieldset>
            <legend className="mb-1 text-sm font-semibold text-ink-soft">Only photos tagged (none picked: all photos)</legend>
            <div className="flex flex-wrap gap-1.5">
              {tagChoices.map((t) => {
                const on = tags.includes(t);
                return (
                  <button key={t} type="button" aria-pressed={on} onClick={() => setTags(on ? tags.filter((x) => x !== t) : [...tags, t])} className={cx("rounded-full px-3 py-1 text-sm font-medium", on ? "bg-brand text-white" : "bg-mist text-ink-soft hover:text-ink")}>
                    {t}
                  </button>
                );
              })}
            </div>
          </fieldset>
        )}
        <Checkbox label="Show captions" checked={captions} onChange={setCaptions} />
        <p className="text-sm text-muted">Photos are grouped by tag, two to a page, with their markup, and the PDF is saved to the job's files.</p>
      </div>
    </Dialog>
  );
}

const DRIVE_LABEL: Record<DriveKind, string> = { document: "Google Doc", spreadsheet: "Google Sheet", presentation: "Google Slides", form: "Google Form", folder: "Drive folder", file: "Drive file" };
const DRIVE_COLOR: Record<DriveKind, string> = { document: "#4285F4", spreadsheet: "#0F9D58", presentation: "#F4B400", form: "#7248B9", folder: "#5F6368", file: "#5F6368" };

/** Live Google Docs, Sheets, and Drive files linked here (they stay in Drive; the link opens them). */
function DriveLinks({ owner, canEdit, category }: { owner: Owner; canEdit: boolean; category: string }) {
  const qc = useQueryClient();
  const queryKey = ["drive-links", owner.kind, owner.id];
  const links = useQuery({ queryKey, queryFn: () => get<DriveLinkItem[]>(`${base(owner)}/drive-links`) });
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const add = useMutation({
    mutationFn: () => post(`${base(owner)}/drive-links`, { title, url, category }),
    onSuccess: () => {
      setAdding(false);
      setTitle("");
      setUrl("");
      qc.invalidateQueries({ queryKey });
    },
  });
  const remove = useMutation({ mutationFn: (id: string) => del(`/api/drive-links/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey }) });
  const shown = (links.data ?? []).filter((l) => !category || l.category === category);
  if (!shown.length && !canEdit) return null;
  return (
    <div className="border-t border-mist px-5 py-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-ink-soft">Google Drive</h3>
        {canEdit && !adding && (
          <button className="text-sm font-semibold text-brand hover:underline" onClick={() => setAdding(true)}>
            Link a Doc, Sheet, or Drive file
          </button>
        )}
      </div>
      {adding && (
        <form
          className="mt-3 grid gap-3 sm:grid-cols-[1fr_2fr_auto] sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          {add.error && <div className="sm:col-span-3"><Alert>{errorMessage(add.error)}</Alert></div>}
          <Field label="Name" value={title} maxLength={200} required onChange={(e) => setTitle(e.target.value)} placeholder="Scope worksheet" />
          <Field label="Link (Share → Copy link in Drive)" value={url} required onChange={(e) => setUrl(e.target.value)} placeholder="https://docs.google.com/…" />
          <div className="flex gap-2">
            <Button type="submit" variant="primary" busy={add.isPending}>
              Add
            </Button>
            <Button variant="quiet" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
      {shown.length > 0 ? (
        <ul className="mt-2 divide-y divide-mist">
          {shown.map((l) => (
            <li key={l.id} className="flex items-center gap-3 py-2 text-sm">
              <span className="grid size-8 shrink-0 place-items-center rounded text-xs font-bold text-white" style={{ background: DRIVE_COLOR[l.kind] }} aria-hidden>
                {l.kind === "folder" ? "▤" : l.kind[0]!.toUpperCase()}
              </span>
              <a href={l.url} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate font-medium hover:text-brand">
                {l.title}
              </a>
              <span className="text-muted">
                {DRIVE_LABEL[l.kind]}
                {l.category ? ` · ${l.category}` : ""}
              </span>
              {canEdit && (
                <button className="text-muted hover:text-danger" aria-label={`Remove ${l.title}`} onClick={() => window.confirm(`Remove the link to ${l.title}? The file stays in Drive.`) && remove.mutate(l.id)}>
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        !adding && <p className="mt-1 text-sm text-muted">Link live Docs and Sheets here; they stay in Google Drive.</p>
      )}
    </div>
  );
}
